import clsx from 'clsx';
import dayjs from 'dayjs';
import countries from 'i18n-iso-countries';
import values from 'lodash/values';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { updateRequestSamplesForm } from 'Apollo/Store/Showroom/UserView/Samples/Form/resolvers';
import { setIsRequestSamplesValid } from 'Apollo/Store/Showroom/UserView/Samples/resolvers';
import { useGetShowroomQuery, useGetShowroomSamplesFormQuery } from 'generated/graphql';

import { useDateLocale } from 'lib/Hooks';

import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';

import DatePicker from 'components/Common/DatePicker';

import { useStyles } from './styles';

export type ComponentProps = {
	showroomId: string;
};

const ShowroomSamplesFormStepsAboutRequest: React.FC<ComponentProps> = ({ showroomId }) => {
	const classes = useStyles();

	const { t } = useTranslation();

	const { data: showroomData } = useGetShowroomQuery({ variables: { id: showroomId } });
	const [language, setLanguage] = React.useState('en');
	const [countryNames, setCountryNames] = React.useState([]);
	const dateLocale = useDateLocale(language);

	React.useEffect(() => {
		if (!showroomData) return;
		setLanguage(showroomData.getShowroom.language);
	}, [showroomData]);

	React.useEffect(() => {
		countries.registerLocale(require(`i18n-iso-countries/langs/${language}.json`));
		setCountryNames(values(countries.getNames(language)));
	}, [language]);

	const {
		data: {
			showroom: {
				userView: {
					samples: {
						form: { returnDate, deliveryDate, streetAdress, city, zipCode, country, state }
					}
				}
			}
		}
	} = useGetShowroomSamplesFormQuery();

	const onChangeDate = React.useCallback(
		(date: dayjs.Dayjs, type: 'delivery' | 'return') => {
			if (type === 'return') updateRequestSamplesForm({ returnDate: date });
			else {
				if (date.isAfter(returnDate)) updateRequestSamplesForm({ deliveryDate: date, returnDate: date.add(1, 'day') });
				else updateRequestSamplesForm({ deliveryDate: date });
			}
		},
		[returnDate]
	);

	React.useEffect(() => {
		if (!returnDate || !deliveryDate || !streetAdress || !city || !zipCode || !country || (country && country === 'US' && !state))
			setIsRequestSamplesValid(false);
		else setIsRequestSamplesValid(true);
	}, [city, country, deliveryDate, returnDate, state, streetAdress, zipCode]);

	return (
		<div className={classes.root}>
			<div className={classes.header}>{t('showroom.samples.form.about')}</div>
			<div className={classes.requestDateBox}>
				<div className={classes.requestDateTitle}>{t('showroom.samples.form.receptionDate')}</div>
				<div className={classes.requestDateInputsBox}>
					<DatePicker
						id="deliveryDateInput"
						time={false}
						value={deliveryDate || null}
						disablePast
						cancelLabel={t('showroom.samples.form.cancel')}
						inputLabel={t('showroom.samples.form.deliveryDate')}
						inputClassName={clsx(classes.requestDateInput, classes.requestDateInputMargin)}
						onChange={date => onChangeDate(date, 'delivery')}
						locale={dateLocale}
					/>
					<DatePicker
						id="returnDateInput"
						time={false}
						minDate={dayjs(deliveryDate).add(1, 'day') || null}
						value={returnDate || ''}
						cancelLabel={t('showroom.samples.form.cancel')}
						inputLabel={t('showroom.samples.form.returnDate')}
						inputClassName={classes.requestDateInput}
						onChange={date => onChangeDate(date, 'return')}
						locale={dateLocale}
					/>
				</div>
			</div>
			<div className={classes.requestPlaceBox}>
				<div className={classes.requestPlaceTitle}>{t('showroom.samples.form.receptionLocation')}</div>
				<TextField
					id="streetAddressInput"
					defaultValue={streetAdress}
					onChange={ev => {
						updateRequestSamplesForm({ streetAdress: ev.target.value });
					}}
					size="small"
					className={classes.requestPlaceInput}
					variant="outlined"
					label={t('showroom.samples.form.address')}
					fullWidth
				/>
				<div className={classes.requestPlaceCityBox}>
					<TextField
						id="cityInput"
						defaultValue={city}
						onChange={ev => updateRequestSamplesForm({ city: ev.target.value })}
						size="small"
						className={clsx(classes.requestPlaceInput, classes.requestPlaceInputMargin)}
						variant="outlined"
						label={t('showroom.samples.form.city')}
						fullWidth
					/>
					<TextField
						id="zipCodeInput"
						defaultValue={zipCode}
						onChange={ev => updateRequestSamplesForm({ zipCode: ev.target.value })}
						size="small"
						className={classes.requestPlaceInput}
						variant="outlined"
						label={t('showroom.samples.form.zipCode')}
						fullWidth
					/>
				</div>

				<Autocomplete
					id="countryInput"
					options={countryNames}
					defaultValue={countries.getName(country, language)}
					renderInput={params => (
						<TextField
							{...params}
							label={t('showroom.samples.form.country')}
							variant="outlined"
							className={classes.requestPlaceInput}
							size="small"
						/>
					)}
					onChange={(_, value) => {
						updateRequestSamplesForm({ country: value ? countries.getAlpha2Code(value, language) : '' });
					}}
				/>

				{country === 'US' ? (
					<TextField
						id="stateInput"
						defaultValue={state}
						onChange={ev => updateRequestSamplesForm({ state: ev.target.value })}
						size="small"
						className={classes.requestPlaceInput}
						variant="outlined"
						label={t('showroom.samples.form.state')}
						fullWidth
					/>
				) : null}
			</div>
		</div>
	);
};

export default React.memo(ShowroomSamplesFormStepsAboutRequest);
